import './App.css'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { LanguageProvider } from '@/context/LanguageContext';
import ProtectedLayout from '@/components/layout/ProtectedLayout';
import ErrorBoundary from '@/components/ui/ErrorBoundary';
import HomePage from '@/pages/HomePage';
import Users from '@/pages/users/Users';
import CreateUser from '@/pages/users/CreateUser';
import EditUser from '@/pages/users/EditUser';
import VisitHistory from '@/pages/visit-history/VisitHistory.tsx';
import Movies from '@/pages/content/Movies.tsx';
import TVShows from '@/pages/content/TVShows.tsx';
import Sports from '@/pages/content/Sports.tsx';
import Documentaries from '@/pages/content/Documentaries.tsx';
import General from '@/pages/settings/General.tsx';
import Application from '@/pages/settings/Application.tsx';
import ChangePassword from '@/pages/settings/ChangePassword.tsx';
import SystemMetrics from '@/pages/settings/SystemMetrics.tsx';
import TVChannels from '@/pages/content/TVChannels.tsx';
import TVChannelsBaseUrl from '@/pages/content/TVChannelsBaseUrl';
import TVChannelsOrganize from '@/pages/content/TVChannelsOrganize';
import TVChannelsImport from '@/pages/content/TVChannelsImport';
import TVChannelsProviders from '@/pages/content/TVChannelsProviders';
import TVChannelsProviderList from '@/pages/content/TVChannelsProviderList';
import TVChannelsFlussonicServers from '@/pages/content/TVChannelsFlussonicServers';
import TVChannelsFlussonicServerDetails from '@/pages/content/TVChannelsFlussonicServerDetails';
import TVChannelAutomations from '@/pages/content/TVChannelAutomations';
import TVChannelAutomationForm from '@/pages/content/TVChannelAutomationForm';
import TVChannelScrapingTasks from '@/pages/content/TVChannelScrapingTasks';
import TVChannelScrapingServers from '@/pages/content/TVChannelScrapingServers';
import TVGuide from '@/pages/content/TVGuide.tsx';
import Issues from '@/pages/issues/Issues.tsx';
import CreditHistory from '@/pages/credits/CreditHistory';
import CreditSalesReport from '@/pages/reports/CreditSalesReport';
import ContentProductivityReport from '@/pages/reports/ContentProductivityReport';
import Categories from '@/pages/content/Categories';
import CreateCategory from '@/pages/content/CreateCategory';
import EditCategory from '@/pages/content/EditCategory';
import CreateTVGuide from '@/pages/content/CreateTVGuide';
import EditTVGuide from '@/pages/content/EditTVGuide';
import CreateTVChannel from '@/pages/content/CreateTVChannel';
import EditTVChannel from '@/pages/content/EditTVChannel';
import CreateDocumentary from '@/pages/content/CreateDocumentary';
import EditDocumentary from '@/pages/content/EditDocumentary';
import CreateSport from '@/pages/content/CreateSport';
import EditSport from '@/pages/content/EditSport';
import CreateMovie from '@/pages/content/CreateMovie';
import EditMovie from '@/pages/content/EditMovie';
import Concerts from '@/pages/content/Concerts';
import CreateConcert from '@/pages/content/CreateConcert';
import EditConcert from '@/pages/content/EditConcert';
import CreateTVShow from '@/pages/content/CreateTVShow';
import EditTVShow from '@/pages/content/EditTVShow';
import TVShowEpisodes from '@/pages/content/TVShowEpisodes';
import RadioStations from '@/pages/content/RadioStations';
import CreateRadioStation from '@/pages/content/CreateRadioStation';
import EditRadioStation from '@/pages/content/EditRadioStation';
import Login from '@/pages/auth/Login';
import { AuthProvider } from '@/context/AuthContext.tsx';
import Countries from '@/pages/content/Countries';
import CreateCountry from '@/pages/content/CreateCountry';
import EditCountry from '@/pages/content/EditCountry';
import Campaigns from '@/pages/ads/Campaigns';
import CreateCampaign from '@/pages/ads/CreateCampaign';
import EditCampaign from '@/pages/ads/EditCampaign';
import ViewCampaign from '@/pages/ads/ViewCampaign';
import { useUiVersionWatcher } from '@/hooks/useUiVersionWatcher.ts';
import Button from '@/components/ui/Button';
import { useBranding } from '@/context/BrandingContext';
import { ReactNode, useEffect } from 'react';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
}

function App() {
  const { updateAvailable } = useUiVersionWatcher(300_000);
  const { branding, isLoading } = useBranding();
  const modules = branding.modules ?? {};
  const isModuleEnabled = (enabled?: boolean) => enabled === true;

  const ModuleRoute = ({ enabled, children }: { enabled: boolean; children: ReactNode }) => {
    if (isLoading) {
      return null;
    }
    if (!enabled) {
      return <Navigate to="/" replace />;
    }
    return children;
  };

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <LanguageProvider>
            <Router>
              <ScrollToTop />
              <Routes>
                <Route path="/login" element={<Login/>}/>
                <Route element={<ProtectedLayout/>}>
                  <Route path="/" element={<HomePage/>}/>
                  <Route path="/users" element={<Users/>}/>
                  <Route path="/users/create" element={<CreateUser/>}/>
                  <Route path="/users/:id/edit" element={<EditUser/>}/>
                  <Route path="/visit-history" element={<VisitHistory/>}/>
                  <Route path="/issues" element={<Issues/>}/>
                  <Route path="/credits-history" element={<CreditHistory/>}/>
                  <Route path="/reports/credit-sales" element={<ModuleRoute enabled={isModuleEnabled(modules.reports)}><CreditSalesReport/></ModuleRoute>}/>
                  <Route path="/reports/content-productivity" element={<ModuleRoute enabled={isModuleEnabled(modules.reports)}><ContentProductivityReport/></ModuleRoute>}/>
                  <Route path='/content/movies' element={<ModuleRoute enabled={isModuleEnabled(modules.movies)}><Movies/></ModuleRoute>}/>
                  <Route path="/content/movies/create" element={<ModuleRoute enabled={isModuleEnabled(modules.movies)}><CreateMovie/></ModuleRoute>}/>
                  <Route path="/content/movies/:id/edit" element={<ModuleRoute enabled={isModuleEnabled(modules.movies)}><EditMovie/></ModuleRoute>}/>
                  <Route path="/content/movies/:movieId" element={<ModuleRoute enabled={isModuleEnabled(modules.movies)}><div>Movie Details</div></ModuleRoute>}/>
                  <Route path="/content/tv-shows" element={<ModuleRoute enabled={isModuleEnabled(modules.tvShows)}><TVShows/></ModuleRoute>}/>
                  <Route path="/content/tv-shows/create" element={<ModuleRoute enabled={isModuleEnabled(modules.tvShows)}><CreateTVShow/></ModuleRoute>}/>
                  <Route path="/content/tv-shows/:id/edit" element={<ModuleRoute enabled={isModuleEnabled(modules.tvShows)}><EditTVShow/></ModuleRoute>}/>
                  <Route path="/content/tv-shows/:id/episodes" element={<ModuleRoute enabled={isModuleEnabled(modules.tvShows)}><TVShowEpisodes/></ModuleRoute>}/>
                  <Route path="/content/tv-shows/:showId" element={<ModuleRoute enabled={isModuleEnabled(modules.tvShows)}><div>TV Show Details</div></ModuleRoute>}/>
                  <Route path="/content/sports" element={<ModuleRoute enabled={isModuleEnabled(modules.sports)}><Sports/></ModuleRoute>}/>
                  <Route path="/content/sports/create" element={<ModuleRoute enabled={isModuleEnabled(modules.sports)}><CreateSport/></ModuleRoute>}/>
                  <Route path="/content/sports/:id/edit" element={<ModuleRoute enabled={isModuleEnabled(modules.sports)}><EditSport/></ModuleRoute>}/>
                  <Route path="/content/documentaries" element={<ModuleRoute enabled={isModuleEnabled(modules.documentaries)}><Documentaries/></ModuleRoute>}/>
                  <Route path="/content/documentaries/create" element={<ModuleRoute enabled={isModuleEnabled(modules.documentaries)}><CreateDocumentary/></ModuleRoute>}/>
                  <Route path="/content/documentaries/:id/edit" element={<ModuleRoute enabled={isModuleEnabled(modules.documentaries)}><EditDocumentary/></ModuleRoute>}/>
                  <Route path="/content/tv-channels" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels)}><TVChannels/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/base-url" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels)}><TVChannelsBaseUrl/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/providers" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels) && isModuleEnabled(modules.iptvProviders)}><TVChannelsProviders/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/providers/:providerId/list" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels) && isModuleEnabled(modules.iptvProviders)}><TVChannelsProviderList/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/flussonic-servers" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels) && isModuleEnabled(modules.flussonicServers)}><TVChannelsFlussonicServers/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/flussonic-servers/:serverId" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels) && isModuleEnabled(modules.flussonicServers)}><TVChannelsFlussonicServerDetails/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/automations" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels) && isModuleEnabled(modules.channelAutomations)}><TVChannelAutomations/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/automations/create" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels) && isModuleEnabled(modules.channelAutomations)}><TVChannelAutomationForm/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/automations/:automationId/edit" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels) && isModuleEnabled(modules.channelAutomations)}><TVChannelAutomationForm/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/scraping" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels) && isModuleEnabled(modules.channelScraping)}><Navigate to="/content/tv-channels/scraping/tasks" replace /></ModuleRoute>}/>
                  <Route path="/content/tv-channels/scraping/tasks" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels) && isModuleEnabled(modules.channelScraping)}><TVChannelScrapingTasks/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/scraping/servers" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels) && isModuleEnabled(modules.channelScraping)}><TVChannelScrapingServers/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/organize" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels)}><TVChannelsOrganize/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/import" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels)}><TVChannelsImport/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/create" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels)}><CreateTVChannel/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/:id/edit" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels)}><EditTVChannel/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/tv-guide" element={<ModuleRoute enabled={isModuleEnabled(modules.tvGuide)}><TVGuide/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/tv-guide/create" element={<ModuleRoute enabled={isModuleEnabled(modules.tvGuide)}><CreateTVGuide/></ModuleRoute>}/>
                  <Route path="/content/tv-channels/tv-guide/:id/edit" element={<ModuleRoute enabled={isModuleEnabled(modules.tvGuide)}><EditTVGuide/></ModuleRoute>}/>
                  <Route path="/content/radio-stations" element={<ModuleRoute enabled={isModuleEnabled(modules.radioStations)}><RadioStations/></ModuleRoute>}/>
                  <Route path="/content/radio-stations/create" element={<ModuleRoute enabled={isModuleEnabled(modules.radioStations)}><CreateRadioStation/></ModuleRoute>}/>
                  <Route path="/content/radio-stations/:id/edit" element={<ModuleRoute enabled={isModuleEnabled(modules.radioStations)}><EditRadioStation/></ModuleRoute>}/>
                  <Route path="/content/countries" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels)}><Countries/></ModuleRoute>}/>
                  <Route path="/content/countries/create" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels)}><CreateCountry/></ModuleRoute>}/>
                  <Route path="/content/countries/:id/edit" element={<ModuleRoute enabled={isModuleEnabled(modules.tvChannels)}><EditCountry/></ModuleRoute>}/>
                  <Route path="/content/concerts" element={<ModuleRoute enabled={isModuleEnabled(modules.concerts)}><Concerts/></ModuleRoute>}/>
                  <Route path="/content/concerts/create" element={<ModuleRoute enabled={isModuleEnabled(modules.concerts)}><CreateConcert/></ModuleRoute>}/>
                  <Route path="/content/concerts/:id/edit" element={<ModuleRoute enabled={isModuleEnabled(modules.concerts)}><EditConcert/></ModuleRoute>}/>
                  <Route path="/content/categories" element={<ModuleRoute enabled={isModuleEnabled(modules.categories)}><Categories/></ModuleRoute>}/>
                  <Route path="/content/categories/create" element={<ModuleRoute enabled={isModuleEnabled(modules.categories)}><CreateCategory/></ModuleRoute>}/>
                  <Route path="/content/categories/:id/edit" element={<ModuleRoute enabled={isModuleEnabled(modules.categories)}><EditCategory/></ModuleRoute>}/>
                  <Route path="/settings/general" element={<General/>}/>
                  <Route path="/settings/application/*" element={<ProtectedRoute role="superadmin"><Application/></ProtectedRoute>}/>
                  <Route path="/settings/metrics" element={<ProtectedRoute role="superadmin"><SystemMetrics/></ProtectedRoute>}/>
                  <Route path="/settings/change-password" element={<ChangePassword/>}/>
                  <Route path="/ads/campaigns" element={<Campaigns/>}/>
                  <Route path="/ads/campaigns/create" element={<CreateCampaign/>}/>
                  <Route path="/ads/campaigns/:id" element={<ViewCampaign/>}/>
                  <Route path="/ads/campaigns/:id/edit" element={<EditCampaign/>}/>
                </Route>
                <Route path="*" element={<Navigate to="/" replace/>}/>
              </Routes>
            </Router>
            {updateAvailable && (
              <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
                <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-lg">
                  <div className="text-sm text-gray-800">
                    Nueva actualización disponible. Recarga para actualizar.
                  </div>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => window.location.reload()}
                  >
                    Recargar
                  </Button>
                </div>
              </div>
            )}
            <ReactQueryDevtools initialIsOpen={false}/>
          </LanguageProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
